import {
  Control,
  Controller,
  FieldErrors,
  UseFormGetValues,
  UseFormResetField,
  UseFormSetValue,
} from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import React, { FC } from 'react';

import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  Box,
  Button,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';

import { IItem } from '../../../types/IItem';

export const CustomServices: FC<{
  servicesFields: string[];
  setServicesFields: React.Dispatch<React.SetStateAction<string[]>>;
  setValue: UseFormSetValue<IItem>;
  removeField: (val: number) => void;
  getValues: UseFormGetValues<IItem>;
  control: Control<IItem>;
  resetField: UseFormResetField<IItem>;
  errors: FieldErrors<IItem>;
  t: (key: string) => string;
  isLoading: boolean;
  servicesLoadError: boolean;
  append: any;
}> = ({
  setServicesFields,
  servicesFields,
  control,
  removeField,
  errors,
  t,
  append,
}) => {
  return (
    <>
      <Box
        component="fieldset"
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          pl: 2,
          pr: 2,
          mb: 1,
        }}
      >
        <Typography component="legend" sx={{ px: 1, fontSize: 12, mb: 0 }}>
          {t('editTicket.service_custom')}
          <Tooltip title={<>{t('editTicket.note_affect_final_payment')}</>}>
            <IconButton color="inherit">
              <HelpOutlineIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Typography>

        <Grid
          container
          spacing={1}
          sx={{
            marginTop: '0 !important',
            marginBottom:
              servicesFields.length > 0 ? '14px !important' : '0 !important',
          }}
        >
          {servicesFields.map((fieldId, index) => {
            return (
              <>
                <Grid key={fieldId} size={{ xs: 8 }}>
                  <Controller
                    control={control}
                    name={`servicesCustom.${index}.custom_name`}
                    rules={{
                      required: t('form.required'),
                    }}
                    render={({ field: controllerField }) => (
                      <TextField
                        {...controllerField}
                        fullWidth
                        size="small"
                        variant="outlined"
                        label={t('editTicket.create_service')}
                        rows={4}
                        error={
                          !!errors?.servicesCustom &&
                          !!errors?.servicesCustom[index]?.custom_name
                        }
                        helperText={
                          !!errors?.servicesCustom &&
                          !!errors?.servicesCustom[index]?.custom_name
                            ? errors?.servicesCustom?.[index]?.custom_name
                                ?.message
                            : ''
                        }
                      />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 3 }}>
                  <Controller
                    control={control}
                    name={`servicesCustom.${index}.price_at_use`}
                    rules={{
                      required: t('form.required'),
                    }}
                    render={({ field }) => {
                      return (
                        <TextField
                          fullWidth
                          type="number"
                          slotProps={{
                            htmlInput: {
                              min: 0,
                              step: 0.01,
                            },
                          }}
                          variant="outlined"
                          size="small"
                          rows={4}
                          {...field}
                          error={
                            !!errors?.servicesCustom &&
                            !!errors?.servicesCustom[index]?.price_at_use
                          }
                          helperText={
                            !!errors?.servicesCustom &&
                            !!errors?.servicesCustom[index]?.price_at_use
                              ? errors?.servicesCustom?.[index]?.price_at_use
                                  ?.message
                              : ''
                          }
                        />
                      );
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 1, sm: 1 }}>
                  <IconButton
                    onClick={() => {
                      setServicesFields((prev) =>
                        prev.filter((item) => item !== fieldId),
                      );
                      removeField(index);
                    }}
                    sx={{
                      '&:hover': {
                        color: 'error.main',
                      },
                    }}
                  >
                    <DeleteForeverIcon fontSize="small" />
                  </IconButton>
                </Grid>
              </>
            );
          })}
        </Grid>

        <Box sx={{ textAlign: 'right', mb: 1 }}>
          <Button
            variant="contained"
            onClick={() => {
              append({
                custom_name: '',
                price_at_use: 0,
                type: 'custom',
                id: null,
              });
              setServicesFields((prev) => [...prev, uuidv4()]);
            }}
          >
            {t('form.btn_add')}
          </Button>
        </Box>
      </Box>
    </>
  );
};
